import React from 'react';
import { FieldValue } from '../../organisms/Form/Form.types';


/**
 * Utility function to check if a given value has content.
 * - If the value is an array, it returns true if the array has elements, otherwise false.
 * - If the value is not an array, it returns the value itself.
 *
 * @param {FieldValue} value - The value to check. Can be a string, number, string array, or undefined.
 * @returns {boolean | FieldValue} - Returns true if the value is a non-empty array, otherwise returns the value itself.
 */
export const hasValue = (value: FieldValue): boolean | FieldValue => {
  // Check if the value is an array
  if (Array.isArray(value)) {
    // Return true if the array has elements, otherwise false
    return value.length > 0;
  }
  // If the value is not an array, return the value itself
  return value;
};

/**
 * Returns the selected value in an appropriate format.
 *
 * @param selected - The selected value, which can be a string, number, or an array of strings or numbers.
 * @param defaultValue - The default value to return if no selected value is provided. Default is an empty string.
 * @param labelMap - Optional map of values to labels for displaying custom text.
 * @returns The selected value if it exists, formatted as a string or number, or the default value.
 */
export const getSelectedValue = (
  selected: FieldValue, 
  defaultValue: string = '', 
  labelMap?: Map<string, React.ReactNode>
): string | React.ReactNode | undefined => {
  if (!selected) return defaultValue;

  // Return the selected value directly if it is a string or number
  if (typeof selected === 'string' || typeof selected === 'number') {
    const key = String(selected);
    return labelMap?.get(key) || selected;
  }

  // If the selected value is an array
  if (Array.isArray(selected) && hasValue(selected)) {
    const [firstItem, ...rest] = selected;
    const key = String(firstItem);
    const firstLabel = labelMap?.get(key) || key;
    return rest.length === 0 ? firstLabel : `${firstLabel} + ${rest.length}`; // Format based on array length
  }

  return defaultValue;
};

/**
   * Utility function to handle changes to the selected value in a Select component.
   * @param newValue  The new value to be selected or deselected.
   * @param Values The current selected value(s).
   * @param multiple Flag to indicate if multiple selections are allowed.
   * @returns The updated selected option(s).
   */
export const handleSelectionChange = (
  newValue: string | number,
  selectedValue: FieldValue,
  multiple?: boolean,
  defaultValue?: FieldValue,
  hasDefaultOption?: boolean,
): FieldValue => {
  let currentSelection: FieldValue;

  // Check if multiple selections are allowed and the current selection is an array.
  if (multiple) {
    if (Array.isArray(selectedValue)) {
      // If the new value is already in the selection, remove it.
      if (selectedValue.includes(String(newValue))) {
        currentSelection = selectedValue.filter((item) => item !== newValue);
      } else {
        // If the new value is not in the selection, add it.
        currentSelection = [...selectedValue, String(newValue)];
      }
    } else {
      currentSelection = [String(newValue)];
    }

  } else {
    // For single selection mode, toggle the selection.
    const isValueUpdated = newValue !== selectedValue;
    const fallBackValue = hasDefaultOption ? (defaultValue || '') : newValue;
    currentSelection = isValueUpdated ? newValue : fallBackValue;
  }

  return currentSelection;
};

/**
 * Utility function to determine if an option is selected.
 * @param optionValue The value of the option to check.
 * @param Values The currently selected value.
 * @returns Whether the option is selected or not.
 */
export const checkIsSelected = (optionValue?: string, selectedValue?: FieldValue) => {
  if (selectedValue === undefined) return false;
  if (optionValue && Array.isArray(selectedValue)) {
    return selectedValue.includes(optionValue);
  }
  return optionValue === selectedValue;
};

